import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { UserCircle, Bank, LockKey, FloppyDisk, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import ChangePasswordDialog from "../components/ChangePasswordDialog";
import DeleteAccountDialog from "../components/DeleteAccountDialog";

const MAX_AVATAR_BYTES = 800 * 1024;

const readFileAsDataURL = (file) => new Promise((res, rej) => {
  const fr = new FileReader();
  fr.onload = () => res(fr.result);
  fr.onerror = rej;
  fr.readAsDataURL(file);
});

// Presentational label showing that a field is read-only + why
const ReadOnlyPill = ({ children }) => (
  <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold border border-white/10 px-2 py-0.5">
    {children}
  </span>
);

export default function ProfileSettings() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    phone: "",
    address: "",
    bio: "",
    avatar_url: "",
    iban: "",
    iban_holder_name: "",
    iban_bank_name: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      phone: user.phone || "",
      address: user.address || "",
      bio: user.bio || "",
      avatar_url: user.avatar_url || "",
      iban: user.iban || "",
      iban_holder_name: user.iban_holder_name || "",
      iban_bank_name: user.iban_bank_name || "",
    });
  }, [user]);

  const isInstructor = (user?.roles || [user?.role]).includes("instructor");

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_AVATAR_BYTES) { toast.error(t("auth.photo_too_large")); return; }
    if (!file.type.startsWith("image/")) { toast.error(t("auth.photo_invalid_type")); return; }
    const dataUrl = await readFileAsDataURL(file);
    setForm((f) => ({ ...f, avatar_url: dataUrl }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/auth/me", {
        phone: form.phone,
        address: form.address,
        bio: form.bio,
        avatar_url: form.avatar_url,
        iban: form.iban,
        iban_holder_name: form.iban_holder_name,
        iban_bank_name: form.iban_bank_name,
      });
      await refresh();
      toast.success(t("profile.saved"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const payoutIncomplete = isInstructor && (!user.iban || !user.iban_holder_name);

  return (
    <div className="min-h-screen bg-[#050505] px-4 sm:px-6 py-10" data-testid="profile-settings-page">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold">{t("profile.tag")}</div>
            <h1 className="font-display text-4xl sm:text-5xl uppercase font-black mt-1 tracking-tight">
              {t("profile.title")}
            </h1>
            <p className="text-zinc-400 text-sm mt-2">{t("profile.description")}</p>
          </div>
        </div>

        {/* Payout reminder (instructors only) */}
        {payoutIncomplete && (
          <div className="border border-[#FFB800]/50 bg-[#FFB800]/5 p-4 flex items-start gap-3" data-testid="payout-missing-banner">
            <WarningCircle size={22} weight="fill" className="text-[#FFB800] shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#FFB800] font-bold">
                {t("profile.payout_incomplete_tag")}
              </div>
              <div className="text-sm text-zinc-300 mt-1">
                {t("profile.payout_incomplete_body")}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-6">
          {/* Section: Personal info */}
          <section className="border border-white/10 bg-[#0A0A0A] p-6" data-testid="section-personal">
            <div className="flex items-center gap-2 mb-6">
              <UserCircle size={20} weight="duotone" className="text-[#E30613]" />
              <h2 className="font-display text-xl uppercase font-black">{t("profile.section_personal")}</h2>
            </div>

            {/* Avatar */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-20 h-20 border border-white/20 bg-black overflow-hidden">
                {form.avatar_url ? (
                  <img src={form.avatar_url} alt="avatar" className="w-full h-full object-cover" data-testid="profile-avatar-preview" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-500">
                    <UserCircle size={44} weight="thin" />
                  </div>
                )}
              </div>
              <label htmlFor="profile-avatar-upload" className="cursor-pointer bg-white/5 hover:bg-white/10 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest font-bold text-white">
                {form.avatar_url ? t("auth.photo_change") : t("auth.photo_choose")}
              </label>
              <input id="profile-avatar-upload" type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="profile-avatar-input" />
            </div>

            {/* Read-only identity fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("profile.field_name")}</Label>
                  <ReadOnlyPill>{t("profile.readonly")}</ReadOnlyPill>
                </div>
                <Input value={user.name || ""} disabled className="bg-black/50 border-white/10 rounded-none h-11 text-zinc-500 mt-1" data-testid="profile-name-readonly" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("profile.field_dob")}</Label>
                  <ReadOnlyPill>{t("profile.readonly")}</ReadOnlyPill>
                </div>
                <Input value={user.date_of_birth || "—"} disabled className="bg-black/50 border-white/10 rounded-none h-11 text-zinc-500 mt-1" data-testid="profile-dob-readonly" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("profile.field_email")}</Label>
                  <ReadOnlyPill>{t("profile.readonly")}</ReadOnlyPill>
                </div>
                <Input value={user.email || ""} disabled className="bg-black/50 border-white/10 rounded-none h-11 text-zinc-500 mt-1" data-testid="profile-email-readonly" />
                <div className="text-[10px] text-zinc-500 mt-1">{t("profile.email_change_hint")}</div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("profile.field_phone")}</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+41 ..." className="bg-black border-white/20 rounded-none h-11 mt-1 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]"
                  data-testid="profile-phone-input" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("profile.field_address")}</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder={t("auth.address_placeholder")}
                  className="bg-black border-white/20 rounded-none h-11 mt-1 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]"
                  data-testid="profile-address-input" />
              </div>
              {isInstructor && (
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("profile.field_bio")}</Label>
                  <Textarea rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                    placeholder={t("auth.bio_placeholder")}
                    className="bg-black border-white/20 rounded-none mt-1 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]"
                    data-testid="profile-bio-input" />
                </div>
              )}
            </div>
          </section>

          {/* Section: Payout (instructors only) */}
          {isInstructor && (
            <section className="border border-white/10 bg-[#0A0A0A] p-6" data-testid="section-payout">
              <div className="flex items-center gap-2 mb-2">
                <Bank size={20} weight="duotone" className="text-[#E30613]" />
                <h2 className="font-display text-xl uppercase font-black">{t("profile.section_payout")}</h2>
                {user.payout_ready && (
                  <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#00FF66] font-bold ml-2">
                    <CheckCircle size={12} weight="fill" />{t("profile.payout_ready")}
                  </span>
                )}
              </div>
              <p className="text-xs text-zinc-400 mb-6 leading-relaxed">{t("profile.payout_hint")}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("profile.field_iban")}</Label>
                  <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase() })}
                    placeholder="CH93 0076 2011 6238 5295 7"
                    className="bg-black border-white/20 rounded-none h-11 mt-1 font-mono uppercase focus-visible:ring-[#E30613] focus-visible:border-[#E30613]"
                    data-testid="profile-iban-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("profile.field_iban_holder")}</Label>
                  <Input value={form.iban_holder_name} onChange={(e) => setForm({ ...form, iban_holder_name: e.target.value })}
                    placeholder={t("profile.field_iban_holder_placeholder")}
                    className="bg-black border-white/20 rounded-none h-11 mt-1 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]"
                    data-testid="profile-iban-holder-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("profile.field_iban_bank")}</Label>
                  <Input value={form.iban_bank_name} onChange={(e) => setForm({ ...form, iban_bank_name: e.target.value })}
                    placeholder="UBS, PostFinance, Raiffeisen…"
                    className="bg-black border-white/20 rounded-none h-11 mt-1 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]"
                    data-testid="profile-iban-bank-input" />
                </div>
              </div>
            </section>
          )}

          {/* Save */}
          <div className="flex items-center justify-end gap-3">
            <Link to={isInstructor ? "/instructor" : "/my-bookings"}>
              <Button type="button" variant="ghost" className="text-xs uppercase tracking-wider font-bold rounded-none h-11">
                {t("common.cancel")}
              </Button>
            </Link>
            <Button type="submit" disabled={saving}
              className="bg-[#E30613] hover:bg-[#FF1A27] text-white uppercase tracking-[0.2em] font-bold rounded-none h-11 px-6"
              data-testid="profile-save-button">
              <FloppyDisk size={14} className="mr-2" weight="bold" />
              {saving ? t("common.loading") : t("profile.save")}
            </Button>
          </div>
        </form>

        {/* Section: Security & Account */}
        <section className="border border-white/10 bg-[#0A0A0A] p-6" data-testid="section-security">
          <div className="flex items-center gap-2 mb-6">
            <LockKey size={20} weight="duotone" className="text-[#E30613]" />
            <h2 className="font-display text-xl uppercase font-black">{t("profile.section_security")}</h2>
          </div>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-sm text-zinc-300">{t("profile.change_password_label")}</div>
              <div className="text-xs text-zinc-500 mt-1">{t("profile.change_password_hint")}</div>
            </div>
            <ChangePasswordDialog />
          </div>
          <div className="mt-6 pt-6 border-t border-red-500/20">
            <div className="text-[10px] uppercase tracking-[0.3em] text-red-400 font-bold mb-3">
              {t("instructor_dash.danger_zone_label")}
            </div>
            <DeleteAccountDialog />
          </div>
        </section>
      </div>
    </div>
  );
}
